import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types';
import {connect} from "react-redux";
import CorrespondenceService from "services/CorrespondenceService";
import ThreadsPanel from "./ThreadsPanel";
import Loading from "components/Loading";
import moment from "libs/moment";
import CorrespondenceSearch from "./CorrespondenceSearch";
import PrivilegeService from "services/PrivilegeService";
import {shareSystemsActive} from "libs/shareSystems";
import QuestionDeadlineSection from "./QuestionDeadlineSection";
import QuestionDeadlineAnswerSection from "./QuestionAnswerDeadlineSection";
import GridBlock from "../../GridBlock";
import {makeStyles} from "@material-ui/core";

const useStyles = makeStyles(theme => ({
    questionSection: {
        marginTop: theme.spacing(3)
    },
    questionSectionItems: {
        maxWidth: '45%'
    }
}))

function Correspondence(
    {
        rfxRound,
        threadsBeforeEndDate,
        threadsAfterEndDate,
        correspondenceSplitDate,
        correspondenceCheckTime,
        correspondencePrivileges,
        contractor,
        demandPrivileges = {},
        demandHeader,
    }) {

    const classes = useStyles();
    const loadingNewInProgress = useRef(false);
    const searchSelected = useRef(false);
    const intervalId = useRef(0);
    const [searchInProgress, setSearchInProgress] = useState(false)
    const [before, setBefore] = useState(!correspondenceCheckTime || !correspondenceSplitDate || moment(correspondenceCheckTime).isBefore(moment(correspondenceSplitDate)))

    const showShare = useMemo(() => demandPrivileges.INTEGRATIONS_UPLOAD && shareSystemsActive(demandHeader).length > 0, [demandHeader, demandPrivileges])

    const doSearch = (searchOb) => {
        setSearchInProgress(true);
        if (Object.keys(searchOb).length) {
            searchSelected.current = true;
        } else {
            searchSelected.current = false;
        }
        let searchPromise;
        if (contractor) {
            searchPromise = CorrespondenceService.getContractorSearchResult(rfxRound.id, searchOb);
        } else {
            searchPromise = CorrespondenceService.getBuyerSearchResult(rfxRound.id, searchOb);
        }
        searchPromise.finally(() => setSearchInProgress(false))
    };

    const checkNewMessages = () => {
        if (!loadingNewInProgress.current && !searchSelected.current) {
            loadingNewInProgress.current = true;
            if (contractor) {
                CorrespondenceService.getContractorNewMessages(rfxRound.id).then(() => loadingNewInProgress.current = false);
            } else {
                CorrespondenceService.getBuyerNewMessages(rfxRound.id).then(() => loadingNewInProgress.current = false);
            }
        }
    }

    useEffect(() => {
        if (contractor) {
            CorrespondenceService.getContractorThreadList(rfxRound.id);
            CorrespondenceService.getContractorPrivileges(rfxRound.id);
        } else {
            CorrespondenceService.getBuyerThreadList(rfxRound.id);
            CorrespondenceService.getBuyerPrivileges(rfxRound.id);
        }
        return () => {
            CorrespondenceService.cleanupThreadList();
            CorrespondenceService.cleanupPrivileges();
        }
        // eslint-disable-next-line
    }, [])


    useEffect(() => {
        intervalId.current = setInterval(checkNewMessages, 30000);
        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
            }
        }
        //eslint-disable-next-line
    },[]);

    const timeout = useRef();

    useEffect(() => {
        if (correspondenceSplitDate && correspondenceCheckTime) {
            const isBefore = moment(correspondenceCheckTime).isBefore(moment(correspondenceSplitDate));
            if (isBefore) {
                if (timeout.current) {
                    clearTimeout(timeout.current);
                }
                const diff = moment(correspondenceSplitDate).diff(correspondenceCheckTime) + 500;
                timeout.current = setTimeout(checkNewMessages, diff)
            } else {

            }
            setBefore(isBefore);
        }

        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
        }
        //eslint-disable-next-line
    }, [correspondenceSplitDate, correspondenceCheckTime]);
    return (
        <>
            <CorrespondenceSearch searchInProgress={searchInProgress} doSearch={doSearch} rfxRoundId={rfxRound.id} contractor={contractor}/>

            {
                (!threadsAfterEndDate || !threadsBeforeEndDate || !correspondenceCheckTime || !correspondencePrivileges) ?
                    <Loading/>
                    :
                    <>
                        {(!!rfxRound.questionEndDate && contractor) &&
                            <div className={classes.questionSection}>
                                <QuestionDeadlineSection currentRfxRound={rfxRound} contractor={contractor}/>
                            </div>
                        }
                        {(!!rfxRound.questionEndDate && !!rfxRound.questionAnswerEndDate && !contractor) &&
                            <GridBlock justify='space-evenly' className={classes.questionSection} itemClassName={classes.questionSectionItems}>
                                <QuestionDeadlineSection currentRfxRound={rfxRound} contractor={contractor} />
                                <QuestionDeadlineAnswerSection currentRfxRound={rfxRound} />
                            </GridBlock>
                        }
                        {!!threadsBeforeEndDate &&
                        <ThreadsPanel
                            contractor={contractor}
                            title={threadsBeforeEndDate.name}
                            currentActive={before}
                            rfxRound={rfxRound}
                            listResourceId={CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID}
                            threadsList={threadsBeforeEndDate.threads}
                            showShare={showShare}
                        />
                        }

                        {!!threadsAfterEndDate &&
                        <ThreadsPanel
                            contractor={contractor}
                            title={threadsAfterEndDate.name}
                            currentActive={!before}
                            rfxRound={rfxRound}
                            listResourceId={CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID}
                            threadsList={threadsAfterEndDate.threads}
                            showShare={showShare}
                        />}

                        {/*{!!threadsNotSpliced &&*/}
                        {/*<ThreadsPanel*/}
                        {/*    contractor={contractor}*/}
                        {/*    title={threadsNotSpliced.name}*/}
                        {/*    currentActive='true'*/}
                        {/*    rfxRound={rfxRound}*/}
                        {/*    listResourceId={CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID}*/}
                        {/*    threadsList={threadsNotSpliced.threads}*/}
                        {/*/>}*/}
                    </>
            }
        </>
    )
}

Correspondence.propTypes = {
    classes: PropTypes.any,
}

const mapStateToProps = (state) => {
    return {
        threadsBeforeEndDate: state.resources[CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID],
        threadsAfterEndDate: state.resources[CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID],
        correspondenceSplitDate: state.resources[CorrespondenceService.CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID],
        correspondenceCheckTime: state.resources[CorrespondenceService.CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID],
        correspondencePrivileges: state.privileges[PrivilegeService.CORRESPONDENCE_PRIVILEGE_STORE_KEY],
        demandPrivileges: state.privileges[PrivilegeService.DEMAND_PRIVILEGE_STORE_KEY],
        demandHeader: state.resources.demandHeader
    }
}
const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(Correspondence);